import {Helmet} from '@dr.pogodin/react-helmet';
import {toSlideImages} from '@online-offer/shared';
import {
  Box,
  Heading,
  HStack,
  Icon,
  Show,
  Stack,
  Text,
  VStack,
  type Integer,
  type ValueByDevice,
} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {parseDate} from 'shared/web';

import {IllustrativePhotoCaption} from '../../components/IllustrativePhotoCaption/IllustrativePhotoCaption';
import {PhotoCarousel} from '../../components/PhotoCarousel/PhotoCarousel';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {getHeroPhotos} from '../../utils/getHeroPhotos';
import {translateWithFallback as t} from '../../utils/translateWithFallback';

const CONTENT_MAX_WIDTH = 432;
const CONTENT_PADDING_HORIZONTAL: ValueByDevice<Integer> = [3, 8, 8, 20];

export function Hero() {
  const {saleVehiclePhotos, illustrativePhoto, vehicle, afterSale} = useGetOnlineOfferData();
  const formatDateTime = useDateTimeFormatter();

  const {photos, isIllustrative} = getHeroPhotos({saleVehiclePhotos, illustrativePhoto});
  const images = toSlideImages(photos);

  return (
    <section id="hero" data-testid="vehicleHero">
      <Helmet>
        <title>{vehicle?.title}</title>
      </Helmet>

      <Box width="100%" overflow="hidden" backgroundColor="fill.white">
        <VStack align="center">
          <Box
            width="100%"
            maxWidth={CONTENT_MAX_WIDTH}
            paddingTop={[4, 3, 3, 3]}
            paddingBottom={[4, 6, 6, 8]}
            paddingHorizontal={CONTENT_PADDING_HORIZONTAL}
          >
            <Stack direction={['column', 'row']} spacing={3} align={['flex-start', 'center']} wrap>
              <Heading size={1}>{vehicle?.title}</Heading>
              <Show when={afterSale}>
                {(sale) => (
                  <Box
                    backgroundColor="semantic.bg.green"
                    borderRadius="border_radius_full"
                    paddingHorizontal={3}
                    paddingVertical={1}
                    data-testid="afterSaleBadge"
                  >
                    <HStack spacing={2} align="center">
                      <Icon value="good" size={4} color="text.successDark" />
                      <Text size="small" color="successDark">
                        {t('afterSale.handedOverOn', 'Vehicle handed over on {date}', {
                          date: formatDateTime('dateShort', parseDate(sale.closedAt)),
                        })}
                      </Text>
                    </HStack>
                  </Box>
                )}
              </Show>
            </Stack>
          </Box>
          <Box width="100%">
            <PhotoCarousel lightboxId="hero" images={images} isIllustrative={isIllustrative} />
          </Box>
          <Show when={isIllustrative}>
            <Box
              width="100%"
              maxWidth={CONTENT_MAX_WIDTH}
              paddingTop={2}
              paddingHorizontal={CONTENT_PADDING_HORIZONTAL}
            >
              <IllustrativePhotoCaption />
            </Box>
          </Show>
        </VStack>
      </Box>
    </section>
  );
}
